import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin } from '../utils/auth';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface BracketMatch {
  id: string;
  roundId: string;
  bracketPosition: number;
  bracketType: string;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
}

interface Round {
  id: string;
  roundNumber: number;
  roundName: string;
  roundType: string;
}

interface TeamStanding {
  teamId: string;
  wins: number;
  losses: number;
  draws: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  goalDifference: number;
}

interface BracketTeam {
  id: string;
  name: string;
  teamNumber?: string;
  seed: number | null;
  groupName: string | null;
}

interface BracketData {
  tournament: {
    id: string;
    name: string;
    format: string;
    status: string;
  };
  rounds: Round[];
  matches: BracketMatch[];
  teams: BracketTeam[];
  standings: TeamStanding[];
}

const TournamentBracket: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<BracketData | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  useEffect(() => {
    const fetchBracket = async () => {
      try {
        const res = await api.get(`/tournaments/${id}/bracket`);
        setData(res.data);
      } catch (err) {
        console.error('Error fetching bracket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBracket();
  }, [id]);

  if (loading) return <LoadingSpinner fullPage />;
  if (!data) return <div className="page-container"><p>Tournament not found.</p></div>;

  const { tournament, rounds, matches, teams, standings } = data;
  const format = tournament.format;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0 }}>{tournament.name}</h1>
          <p style={{ color: '#6c757d', margin: '4px 0' }}>{tournament.status}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isAdmin && <Link to={`/tournaments/${id}/manage`} className="btn btn-outline-secondary">Manage</Link>}
          <Link to="/tournaments" className="btn btn-outline-secondary">Back</Link>
        </div>
      </div>

      {format === 'round_robin' ? (
        <RoundRobinView teams={teams} matches={matches} standings={standings} rounds={rounds} />
      ) : format === 'group_knockout' ? (
        <GroupKnockoutView teams={teams} matches={matches} standings={standings} rounds={rounds} />
      ) : (
        <EliminationBracketView matches={matches} rounds={rounds} format={format} />
      )}
    </div>
  );
};

// ============== Elimination Bracket (Single / Double) ==============

const EliminationBracketView: React.FC<{
  matches: BracketMatch[];
  rounds: Round[];
  format: string;
}> = ({ matches, rounds, format }) => {
  const winnersRounds = rounds.filter(r => r.roundType === 'winners' || r.roundType === 'final');
  const losersRounds = rounds.filter(r => r.roundType === 'losers');
  const grandFinalRound = rounds.find(r => r.roundType === 'final' && format === 'double_elimination');

  return (
    <div>
      {/* Winners Bracket */}
      <h2 style={{ marginBottom: '16px' }}>{format === 'double_elimination' ? 'Winners Bracket' : 'Bracket'}</h2>
      <div style={{ display: 'flex', gap: '24px', overflowX: 'auto', paddingBottom: '20px' }}>
        {winnersRounds.map(round => {
          const roundMatches = matches
            .filter(m => m.roundId === round.id && (m.bracketType === 'winners' || !m.bracketType))
            .sort((a, b) => a.bracketPosition - b.bracketPosition);

          return (
            <div key={round.id} style={{ minWidth: '220px' }}>
              <h4 style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}>
                {round.roundName}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'space-around', minHeight: '100%' }}>
                {roundMatches.map(match => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Losers Bracket (Double Elimination) */}
      {format === 'double_elimination' && losersRounds.length > 0 && (
        <>
          <h2 style={{ marginTop: '32px', marginBottom: '16px' }}>Losers Bracket</h2>
          <div style={{ display: 'flex', gap: '24px', overflowX: 'auto', paddingBottom: '20px' }}>
            {losersRounds.map(round => {
              const roundMatches = matches
                .filter(m => m.roundId === round.id)
                .sort((a, b) => a.bracketPosition - b.bracketPosition);

              return (
                <div key={round.id} style={{ minWidth: '220px' }}>
                  <h4 style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}>
                    {round.roundName}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'space-around' }}>
                    {roundMatches.map(match => (
                      <MatchCard key={match.id} match={match} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Grand Final */}
      {grandFinalRound && (
        <>
          <h2 style={{ marginTop: '32px', marginBottom: '16px' }}>Grand Final</h2>
          <div style={{ maxWidth: '280px' }}>
            {matches.filter(m => m.bracketType === 'grand_final').map(match => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============== Match Card ==============

const MatchCard: React.FC<{ match: BracketMatch }> = ({ match }) => {
  const isCompleted = match.status === 'completed';
  const homeWins = isCompleted && match.homeScore != null && match.awayScore != null && match.homeScore > match.awayScore;
  const awayWins = isCompleted && match.homeScore != null && match.awayScore != null && match.awayScore > match.homeScore;

  return (
    <div style={{
      border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden',
      backgroundColor: isCompleted ? '#f8f9fa' : 'white',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
        borderBottom: '1px solid #eee',
        fontWeight: homeWins ? 700 : 400,
        backgroundColor: homeWins ? '#d1e7dd' : 'transparent',
      }}>
        <span style={{ fontSize: '13px' }}>{match.homeTeam?.name || 'TBD'}</span>
        <span style={{ fontSize: '13px', minWidth: '20px', textAlign: 'right' }}>
          {isCompleted ? match.homeScore : ''}
        </span>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
        fontWeight: awayWins ? 700 : 400,
        backgroundColor: awayWins ? '#d1e7dd' : 'transparent',
      }}>
        <span style={{ fontSize: '13px' }}>{match.awayTeam?.name || 'TBD'}</span>
        <span style={{ fontSize: '13px', minWidth: '20px', textAlign: 'right' }}>
          {isCompleted ? match.awayScore : ''}
        </span>
      </div>
    </div>
  );
};

// ============== Round Robin View ==============

const RoundRobinView: React.FC<{
  teams: BracketTeam[];
  matches: BracketMatch[];
  standings: TeamStanding[];
  rounds: Round[];
}> = ({ teams, matches, standings, rounds }) => {
  const sortedStandings = [...standings].sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference);
  const teamNameMap = new Map(teams.map(t => [t.id, t.name]));

  return (
    <div>
      {/* Standings Table */}
      <h2 style={{ marginBottom: '12px' }}>Standings</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '32px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #dee2e6' }}>
            <th style={{ padding: '8px', textAlign: 'left' }}>#</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>Team</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>P</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>W</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>D</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>L</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>GF</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>GA</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>GD</th>
            <th style={{ padding: '8px', textAlign: 'center' }}>Pts</th>
          </tr>
        </thead>
        <tbody>
          {sortedStandings.map((s, idx) => (
            <tr key={s.teamId} style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '8px' }}>{idx + 1}</td>
              <td style={{ padding: '8px' }}>{teamNameMap.get(s.teamId) || 'Unknown'}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.wins + s.draws + s.losses}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.wins}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.draws}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.losses}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.goalsFor}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.goalsAgainst}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>{s.goalDifference}</td>
              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 700 }}>{s.points}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Round-by-round matches */}
      <h2 style={{ marginBottom: '12px' }}>Matches by Round</h2>
      {rounds.map(round => {
        const roundMatches = matches.filter(m => m.roundId === round.id);
        return (
          <div key={round.id} style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#495057', marginBottom: '8px' }}>{round.roundName}</h3>
            {roundMatches.map(match => (
              <div key={match.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
              }}>
                <span style={{ flex: 1, textAlign: 'right', fontWeight: match.status === 'completed' && match.homeScore! > match.awayScore! ? 700 : 400 }}>
                  {match.homeTeam?.name || 'TBD'}
                </span>
                <span style={{ fontWeight: 600, minWidth: '60px', textAlign: 'center' }}>
                  {match.status === 'completed' ? `${match.homeScore} - ${match.awayScore}` : 'vs'}
                </span>
                <span style={{ flex: 1, fontWeight: match.status === 'completed' && match.awayScore! > match.homeScore! ? 700 : 400 }}>
                  {match.awayTeam?.name || 'TBD'}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ============== Group + Knockout View ==============

const GroupKnockoutView: React.FC<{
  teams: BracketTeam[];
  matches: BracketMatch[];
  standings: TeamStanding[];
  rounds: Round[];
}> = ({ teams, matches, standings, rounds }) => {
  const groupRounds = rounds.filter(r => r.roundType === 'group');
  const knockoutRounds = rounds.filter(r => r.roundType === 'winners' || r.roundType === 'final');

  // Group teams by group name
  const groups = new Map<string, BracketTeam[]>();
  teams.forEach(team => {
    const group = team.groupName || 'Ungrouped';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(team);
  });

  const teamNameMap = new Map(teams.map(t => [t.id, t.name]));

  return (
    <div>
      {/* Group Standings */}
      <h2 style={{ marginBottom: '16px' }}>Group Stage</h2>
      {Array.from(groups.entries()).sort().map(([groupName, groupTeams]) => {
        const groupTeamIds = new Set(groupTeams.map(t => t.id));
        const groupStandings = standings
          .filter(s => groupTeamIds.has(s.teamId))
          .sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference);

        return (
          <div key={groupName} style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>Group {groupName}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', fontSize: '13px' }}>
                  <th style={{ padding: '6px', textAlign: 'left' }}>Team</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>P</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>W</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>D</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>L</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>GD</th>
                  <th style={{ padding: '6px', textAlign: 'center' }}>Pts</th>
                </tr>
              </thead>
              <tbody>
                {groupStandings.map(s => (
                  <tr key={s.teamId} style={{ borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                    <td style={{ padding: '6px' }}>{teamNameMap.get(s.teamId) || 'Unknown'}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{s.wins + s.draws + s.losses}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{s.wins}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{s.draws}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{s.losses}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{s.goalDifference}</td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 700 }}>{s.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Group Matches */}
      {groupRounds.length > 0 && (
        <>
          <h3 style={{ marginBottom: '12px' }}>Group Matches</h3>
          {groupRounds.map(round => {
            const roundMatches = matches.filter(m => m.roundId === round.id);
            return (
              <div key={round.id} style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', color: '#6c757d', marginBottom: '6px' }}>{round.roundName}</h4>
                {roundMatches.map(match => (
                  <div key={match.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 12px',
                    borderBottom: '1px solid #f0f0f0', fontSize: '14px',
                  }}>
                    <span style={{ flex: 1, textAlign: 'right' }}>{match.homeTeam?.name || 'TBD'}</span>
                    <span style={{ fontWeight: 600, minWidth: '50px', textAlign: 'center' }}>
                      {match.status === 'completed' ? `${match.homeScore} - ${match.awayScore}` : 'vs'}
                    </span>
                    <span style={{ flex: 1 }}>{match.awayTeam?.name || 'TBD'}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}

      {/* Knockout Bracket */}
      {knockoutRounds.length > 0 && (
        <>
          <h2 style={{ marginTop: '32px', marginBottom: '16px' }}>Knockout Stage</h2>
          <div style={{ display: 'flex', gap: '24px', overflowX: 'auto', paddingBottom: '20px' }}>
            {knockoutRounds.map(round => {
              const roundMatches = matches
                .filter(m => m.roundId === round.id)
                .sort((a, b) => a.bracketPosition - b.bracketPosition);

              return (
                <div key={round.id} style={{ minWidth: '220px' }}>
                  <h4 style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}>
                    {round.roundName}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'space-around' }}>
                    {roundMatches.map(match => (
                      <MatchCard key={match.id} match={match} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentBracket;
